import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';

// ── Types ─────────────────────────────────────────────────────────────────────

type SubmitStatus = 'idle' | 'loading' | 'success' | 'error';

// ── Data ──────────────────────────────────────────────────────────────────────

const budgetOptions = [
  '2,000만 원 ~ 3,000만 원',
  '3,000만 원 ~ 4,000만 원',
  '4,000만 원 ~ 5,000만 원',
  '5,000만 원 이상',
  '미정 / 상담 후 결정',
];

const regionOptions = [
  '서울 강남 / 서초',
  '서울 홍대 / 마포',
  '서울 이태원 / 용산',
  '서울 기타',
  '경기 / 인천',
  '부산 / 경남',
  '대구 / 경북',
  '대전 / 충청',
  '광주 / 전라',
  '기타 지역',
];

const planOptions = [
  { value: 'standard', label: 'Type A — 실속형 (3,600만 원)' },
  { value: 'gallery', label: 'Type B — 프리미엄형 (4,800만 원)' },
  { value: 'self', label: 'Type C — 자율시공형 (2,300만 원+α)' },
  { value: 'undecided', label: '미정 / 상담 후 결정' },
];

const contactMethods = ['전화', '카카오톡', '이메일'];

// ── Inquiry Form ──────────────────────────────────────────────────────────────

function InquiryForm() {
  const { ref, visible } = useScrollAnimation(0.05);
  const [status, setStatus] = useState<SubmitStatus>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (status === 'loading') return;

    const form = e.currentTarget;
    const message = (form.elements.namedItem('message') as HTMLTextAreaElement)?.value ?? '';
    if (message.length > 500) return;

    setStatus('loading');

    const data = new URLSearchParams();
    const fields = ['name', 'phone', 'email', 'region', 'budget', 'plan', 'contactMethod', 'openTime', 'message'];
    fields.forEach((field) => {
      const el = form.elements.namedItem(field) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null;
      if (el) data.append(field, el.value);
    });

    try {
      const res = await fetch('https://readdy.ai/api/form/d786llprcamcd36daua0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section className="py-20 md:py-28 bg-[#FAFAFA]" ref={ref}>
      <div className="max-w-[1440px] mx-auto px-8 md:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_520px] gap-16 items-start">

          {/* ── Left: Info ── */}
          <div
            className="transition-all duration-1000 ease-out"
            style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)' }}
          >
            <p className="text-xs tracking-[0.35em] uppercase text-[#C9A96E] mb-5" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 500 }}>Franchise Inquiry</p>
            <h2
              className="text-3xl md:text-4xl text-[#222222] mb-6 leading-tight"
              style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300, letterSpacing: '0.08em' }}
            >
              선율과 함께하는<br />
              <span className="text-[#AAAAAA]">첫 걸음을 시작하세요</span>
            </h2>
            <p className="text-sm text-[#888] leading-relaxed mb-12" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif", lineHeight: '1.75' }}>
              문의 접수 후 24시간 이내에 전담 컨설턴트가 연락드립니다.<br />
              부담 없이 궁금한 점을 남겨주세요.
            </p>

            {/* Contact info cards */}
            <div className="flex flex-col gap-4 mb-12">
              {[
                { icon: 'ri-phone-line', label: '대표 전화', value: '02-1234-5678', sub: '평일 10:00 ~ 18:00' },
                { icon: 'ri-kakao-talk-line', label: '카카오톡 채널', value: '@선율라인스튜디오', sub: '24시간 메시지 접수' },
                { icon: 'ri-mail-line', label: '이메일', value: 'franchise@seonyul.kr', sub: '24시간 이내 회신' },
              ].map((item) => (
                <div key={item.label} className="flex items-start gap-4 p-5 bg-white border border-[#EBEBEB]">
                  <div className="w-10 h-10 flex items-center justify-center border border-[#EBEBEB] flex-shrink-0">
                    <i className={`${item.icon} text-[#C9A96E] text-lg`} />
                  </div>
                  <div>
                    <p className="text-[10px] tracking-[0.2em] uppercase text-[#AAAAAA] mb-0.5">{item.label}</p>
                    <p className="text-sm font-semibold text-[#1A1A1A]">{item.value}</p>
                    <p className="text-[11px] text-[#AAAAAA] mt-0.5">{item.sub}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Quick links */}
            <div className="flex flex-col gap-3">
              <p className="text-[10px] tracking-[0.25em] uppercase text-[#AAAAAA] mb-1">관련 페이지</p>
              {[
                { label: '창업 안내 & 비용', href: '/franchise/guide', icon: 'ri-file-list-3-line' },
                { label: '인테리어 컨셉', href: '/franchise/interior', icon: 'ri-store-2-line' },
                { label: '가맹점 예상 수익', href: '/franchise/revenue', icon: 'ri-bar-chart-2-line' },
              ].map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className="flex items-center gap-3 text-sm text-[#555] hover:text-[#C9A96E] transition-colors duration-200 cursor-pointer group"
                >
                  <i className={`${link.icon} text-[#AAAAAA] group-hover:text-[#C9A96E] transition-colors duration-200`} />
                  {link.label}
                  <i className="ri-arrow-right-s-line ml-auto text-[#DDDDDD] group-hover:text-[#C9A96E] transition-colors duration-200" />
                </Link>
              ))}
            </div>
          </div>

          {/* ── Right: Form ── */}
          <div
            className="transition-all duration-1000 ease-out"
            style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)', transitionDelay: '200ms' }}
          >
            {status === 'success' ? (
              <div className="bg-white border border-[#EBEBEB] p-12 text-center">
                <div className="w-16 h-16 flex items-center justify-center border border-[#C9A96E] mx-auto mb-6">
                  <i className="ri-check-line text-2xl text-[#C9A96E]" />
                </div>
                <h3
                  className="text-2xl text-[#1A1A1A] mb-3"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 400 }}
                >
                  문의가 접수되었습니다
                </h3>
                <p className="text-sm text-[#888] leading-relaxed mb-8">
                  24시간 이내에 전담 컨설턴트가<br />
                  선택하신 연락 방법으로 연락드리겠습니다.
                </p>
                <button
                  onClick={() => setStatus('idle')}
                  className="px-8 py-3 border border-[#1A1A1A] text-[#1A1A1A] text-xs tracking-[0.15em] uppercase font-medium hover:bg-[#1A1A1A] hover:text-white transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  추가 문의하기
                </button>
              </div>
            ) : (
              <form
                data-readdy-form
                onSubmit={handleSubmit}
                className="bg-white border border-[#EBEBEB] p-8 md:p-10 flex flex-col gap-6"
              >
                <div className="pb-5 border-b border-[#F0F0F0]">
                  <p className="text-xs tracking-[0.25em] uppercase text-[#AAAAAA]">가맹 문의 신청서</p>
                </div>

                {/* 이름 */}
                <div>
                  <label className="block text-xs font-semibold text-[#1A1A1A] mb-2">
                    이름 <span className="text-[#C9A96E]">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="성함을 입력해주세요"
                    className="w-full px-4 py-3 border border-[#EBEBEB] text-sm text-[#1A1A1A] placeholder-[#CCCCCC] focus:outline-none focus:border-[#1A1A1A] transition-colors duration-200 bg-[#FAFAFA]"
                  />
                </div>

                {/* 연락처 + 이메일 */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-[#1A1A1A] mb-2">
                      연락처 <span className="text-[#C9A96E]">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      required
                      placeholder="010-0000-0000"
                      className="w-full px-4 py-3 border border-[#EBEBEB] text-sm text-[#1A1A1A] placeholder-[#CCCCCC] focus:outline-none focus:border-[#1A1A1A] transition-colors duration-200 bg-[#FAFAFA]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-[#1A1A1A] mb-2">이메일</label>
                    <input
                      type="email"
                      name="email"
                      placeholder="example@email.com"
                      className="w-full px-4 py-3 border border-[#EBEBEB] text-sm text-[#1A1A1A] placeholder-[#CCCCCC] focus:outline-none focus:border-[#1A1A1A] transition-colors duration-200 bg-[#FAFAFA]"
                    />
                  </div>
                </div>

                {/* 희망 지역 */}
                <div>
                  <label className="block text-xs font-semibold text-[#1A1A1A] mb-2">
                    희망 지역 <span className="text-[#C9A96E]">*</span>
                  </label>
                  <select
                    name="region"
                    required
                    defaultValue=""
                    className="w-full px-4 py-3 border border-[#EBEBEB] text-sm text-[#1A1A1A] focus:outline-none focus:border-[#1A1A1A] transition-colors duration-200 bg-[#FAFAFA] cursor-pointer appearance-none"
                    style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23AAAAAA' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center' }}
                  >
                    <option value="" disabled>지역을 선택해주세요</option>
                    {regionOptions.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>

                {/* 예산 */}
                <div>
                  <label className="block text-xs font-semibold text-[#1A1A1A] mb-2">
                    창업 예산 <span className="text-[#C9A96E]">*</span>
                  </label>
                  <select
                    name="budget"
                    required
                    defaultValue=""
                    className="w-full px-4 py-3 border border-[#EBEBEB] text-sm text-[#1A1A1A] focus:outline-none focus:border-[#1A1A1A] transition-colors duration-200 bg-[#FAFAFA] cursor-pointer appearance-none"
                    style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23AAAAAA' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center' }}
                  >
                    <option value="" disabled>예산 범위를 선택해주세요</option>
                    {budgetOptions.map((b) => (
                      <option key={b} value={b}>{b}</option>
                    ))}
                  </select>
                </div>

                {/* 관심 플랜 */}
                <div>
                  <label className="block text-xs font-semibold text-[#1A1A1A] mb-2">관심 창업 플랜</label>
                  <div className="grid grid-cols-1 gap-2">
                    {planOptions.map((plan) => (
                      <label key={plan.value} className="flex items-center gap-3 p-3 border border-[#EBEBEB] cursor-pointer hover:border-[#C9A96E] transition-colors duration-200 group">
                        <input
                          type="radio"
                          name="plan"
                          value={plan.label}
                          className="accent-[#C9A96E] flex-shrink-0"
                        />
                        <span className="text-xs text-[#555] group-hover:text-[#1A1A1A] transition-colors duration-200">{plan.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* 희망 오픈 시기 */}
                <div>
                  <label className="block text-xs font-semibold text-[#1A1A1A] mb-2">희망 오픈 시기</label>
                  <input
                    type="text"
                    name="openTime"
                    placeholder="예: 2025년 상반기, 6개월 이내 등"
                    className="w-full px-4 py-3 border border-[#EBEBEB] text-sm text-[#1A1A1A] placeholder-[#CCCCCC] focus:outline-none focus:border-[#1A1A1A] transition-colors duration-200 bg-[#FAFAFA]"
                  />
                </div>

                {/* 선호 연락 방법 */}
                <div>
                  <label className="block text-xs font-semibold text-[#1A1A1A] mb-2">선호 연락 방법</label>
                  <div className="flex gap-3 flex-wrap">
                    {contactMethods.map((method) => (
                      <label key={method} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="contactMethod"
                          value={method}
                          defaultChecked={method === '전화'}
                          className="accent-[#C9A96E]"
                        />
                        <span className="text-sm text-[#555]">{method}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* 문의 내용 */}
                <div>
                  <label className="block text-xs font-semibold text-[#1A1A1A] mb-2">문의 내용</label>
                  <textarea
                    name="message"
                    rows={4}
                    maxLength={500}
                    placeholder="궁금하신 점이나 전달하고 싶은 내용을 자유롭게 작성해주세요."
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full px-4 py-3 border border-[#EBEBEB] text-sm text-[#1A1A1A] placeholder-[#CCCCCC] focus:outline-none focus:border-[#1A1A1A] transition-colors duration-200 bg-[#FAFAFA] resize-none"
                  />
                  <p className="text-[11px] text-[#CCCCCC] text-right mt-1">{charCount} / 500</p>
                </div>

                {/* 개인정보 동의 */}
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" name="agree" required className="accent-[#C9A96E] mt-0.5 flex-shrink-0" />
                  <span className="text-xs text-[#888] leading-relaxed">
                    개인정보 수집 및 이용에 동의합니다. 수집된 정보는 가맹 상담 목적으로만 사용되며, 상담 완료 후 즉시 파기됩니다.
                    <span className="text-[#C9A96E]"> (필수)</span>
                  </span>
                </label>

                {/* Error message */}
                {status === 'error' && (
                  <p className="text-xs text-red-500 text-center">
                    제출 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.
                  </p>
                )}

                {/* Submit */}
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="w-full py-4 bg-[#1A1A1A] text-white text-xs tracking-[0.2em] uppercase font-medium hover:bg-[#333] transition-colors duration-300 cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {status === 'loading' ? (
                    <>
                      <i className="ri-loader-4-line animate-spin" />
                      제출 중...
                    </>
                  ) : (
                    <>
                      가맹 문의 신청하기
                      <i className="ri-arrow-right-line" />
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Hero ──────────────────────────────────────────────────────────────────────

function InquiryHero() {
  const { ref, visible } = useScrollAnimation(0.1);

  return (
    <section className="relative pt-40 pb-24 bg-[#1A1A1A] overflow-hidden" ref={ref}>
      <div className="absolute inset-0 opacity-50">
        <img
          src="https://public.readdy.ai/ai/img_res/d7062bf42d1952bfd49635e2ddf1dc4c.jpg"
          alt=""
          className="w-full h-full object-cover object-top"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-[#1A1A1A]/30 via-transparent to-[#1A1A1A]/60" />

      <div className="relative z-10 max-w-[1440px] mx-auto px-8 md:px-16 text-center">
        <div
          className="flex items-center justify-center gap-2 mb-10 transition-all duration-700 ease-out"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)' }}
        >
          <Link to="/franchise" className="text-[10px] tracking-[0.3em] uppercase text-white/30 hover:text-white/60 transition-colors cursor-pointer" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Franchise
          </Link>
          <span className="text-white/20 text-xs">/</span>
          <span className="text-[10px] tracking-[0.3em] uppercase text-[#C9A96E]" style={{ fontFamily: "'Montserrat', sans-serif" }}>가맹 문의</span>
        </div>

        <div
          className="transition-all duration-1000 ease-out"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(30px)', transitionDelay: '150ms' }}
        >
          <h1
            className="text-4xl md:text-6xl text-white mb-6 leading-tight"
            style={{ fontFamily: "'Cinzel', 'Nanum Myeongjo', serif", fontWeight: 300, letterSpacing: '0.1em' }}
          >
            가맹 문의
          </h1>
          <p className="text-sm md:text-base text-white/50 leading-relaxed max-w-lg mx-auto" style={{ fontFamily: "'Montserrat', 'Noto Sans KR', sans-serif", lineHeight: '1.8' }}>
            선율 전담 컨설턴트가 창업의 처음부터 끝까지<br />
            맞춤형으로 안내해 드립니다.
          </p>
        </div>

        <div
          className="mx-auto mt-12 h-px bg-white/15 transition-all duration-1000 ease-out"
          style={{ width: visible ? '80px' : '0px', transitionDelay: '500ms' }}
        />

        <div
          className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-10 md:gap-20 transition-all duration-1000 ease-out"
          style={{ opacity: visible ? 1 : 0, transitionDelay: '600ms' }}
        >
          {[
            { value: '24시간', label: '이내 연락' },
            { value: '무료', label: '초기 상담' },
            { value: '1:1', label: '전담 컨설턴트' },
          ].map((stat) => (
            <div key={stat.label} className="text-center">
              <p className="text-2xl md:text-3xl text-white mb-1" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 700, letterSpacing: '-0.01em' }}>
                {stat.value}
              </p>
              <p className="text-[10px] tracking-[0.25em] uppercase text-white/30" style={{ fontFamily: "'Montserrat', sans-serif" }}>{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function InquiryPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        <InquiryHero />
        <InquiryForm />
      </main>
      <Footer />
    </div>
  );
}
